// #region angular imports
import { 
    AfterContentChecked
    , Component
    , computed
    , DoCheck
    , DOCUMENT
    , ElementRef
    , HostListener
    , inject
    , OnDestroy
    , OnInit
    , Renderer2
    , signal
    , TemplateRef
    , ViewChild 
} from '@angular/core';
import {
    Router
    , NavigationStart
    , NavigationEnd
    , NavigationCancel
    , NavigationCancellationCode
    , NavigationError
    , NavigationSkipped
    , ActivatedRoute
    , Event as RouterEventType
} from '@angular/router';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { 
    faUser
    , faQuestionCircle
    , faShelves
    , faHouse
    , faStaffSnake
    , faArrowRight
    , faSwap
    , faBarcodeRead
    , faGear 
} from '@fortawesome/pro-solid-svg-icons';
import { SwUpdate } from '@angular/service-worker';
import { datadogLogs } from '@datadog/browser-logs';
import { User as ddUser } from '@datadog/browser-core';
import { HttpClient } from '@angular/common/http';
import { filter, map } from 'rxjs/operators';
// #endregion

// #region S2 imports
import { SetLocaldbTablesService } from '../app/services/set-localdb-tables.service'
import { GlobalService } from './shared/service/global.service';
import { SecurityService } from './core/security/security.service';
import { UserJsVm } from './shared/generated/Admin/Models/User/UserJsVm';
import { pcgSettings } from './shared/generated/pcg-settings';
import { NavigationService } from './shared/navigation/navigation.service';
import { GlobalVariablesService } from './services/global-variables.service';
import { AppDatabase } from '../local-db/services/init.ldb.service';
import { PermissionProfileEnum } from './core/enums/generated/PermissionProfileEnum';
import { PermissionRowIds, profileIsViewOnly } from './core/security/permission-profiles';
import { CheckForUpdateService } from './services/check-for-update.service';
import { InactivityTimeoutService } from './core/inactivity-timeout/inactivity-timeout.service';
import { UserTypeEnum } from './core/enums/generated/UserTypeEnum';
import { NotificationService } from './services/notification.service';
import { SelectItem } from './shared/models/select-item';
import { SwapClientVm } from './shared/generated/Admin/Models/SwapClientVm';
import { ThemeService } from './services/theme.service';
import { UserThemeEnum } from './core/enums/generated/UserThemeEnum';
// #endregion

/**
 * What a router event means for the app-wide loading spinner: true when a navigation starts, false
 * when it is over, null when it changes nothing.
 *
 * S2N-3212: a navigation cancelled because a newer navigateByUrl replaced it is not over - the
 * replacement emits its own End/Cancel/Error, or Skipped when it targets the current URL. Ending
 * loading there rebuilt the whole tree on the route being left: deleting a transfer opened from the
 * mobile PQA rebuilt the deleted transfer, whose not-found redirect was bounced home again, and so
 * on in a loop. Guard redirects (code Redirect) deliberately still end loading as they always have;
 * only the superseded case was needed for the fix, and the change is kept that narrow.
 */
export function navigationLoadingState(e: RouterEventType): boolean | null {
    if (e instanceof NavigationStart) { return true; }
    if (
        e instanceof NavigationCancel
        && e.code === NavigationCancellationCode.SupersededByNewNavigation
    ) { return null; }
    if (
        e instanceof NavigationEnd
        || e instanceof NavigationCancel
        || e instanceof NavigationError
        || e instanceof NavigationSkipped
    ) { return false; }
    return null;
}

/**
 * This is the application's root component.
*/
@Component({
    selector: 'pcg-root'
    , templateUrl: './app.component.html'
    , styleUrls: ['./app.component.scss']
    , standalone: false
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy, DoCheck {

    public globalService = inject(GlobalService);
    public gvs = inject(GlobalVariablesService);
    swUpdate = inject(SwUpdate);
    router = inject(Router);
    route = inject(ActivatedRoute);
    sec = inject(SecurityService);
    modalConfig = inject(NgbModalConfig);
    elRef = inject(ElementRef);		
    localDb = inject(AppDatabase);
    localDbService = inject(SetLocaldbTablesService);
    http = inject(HttpClient);
    sw = inject(CheckForUpdateService);
    inactiveTimeout = inject(InactivityTimeoutService);
    ns = inject(NotificationService);
    modalService = inject(NgbModal);
    themeService = inject(ThemeService);
    renderer = inject(Renderer2);
    document = inject(DOCUMENT);

	@ViewChild('tabPanel') tabPanel: any;
	@ViewChild('modalChangeClient', { static: true }) modalChangeClient!: TemplateRef<any>;
	@ViewChild('agencySearch') agencySearch?: ElementRef<HTMLInputElement>;

	updateCheckText = '';
	fileLogoUrl = "../assets/images/logos/logo_small.png";
	footerType!: string;
	clientName: string = "";
	mode = pcgSettings.mode; //for responsive BETA text

    isLoggedIn = signal<boolean>(false); 
    isLoading = signal<boolean>(false); 

    // Mobile footer active-route state. Set by setActiveFooterItem() on every completed
    // navigation. Help has no route (it opens the external knowledge base in a new tab),
    // so it never becomes active.
    isOnHome = false;
    isOnInventories = false;
    isOnProfile = false;
    isOnHelp = false;
	isMobile = signal<boolean>(false);
	onChangePasswordScreen!: boolean;
	isSysAdmin = false;
    isAdmin = false;
    isManager = false;
    isProvider = false;
    isWitness = signal<boolean>(false);
    /**
     * S2N-3076: mirrored onto <body> as the `s2-view-only` class so page-level styling (the banner
     * offset, hidden write affordances) can key on the profile without every component asking.
     * Presentation only — the server refuses the writes regardless.
     */
    isViewOnly = false;
    isApiUser = false;
	showSwapClient = false;

	currentClientId = 0;

	// My Profile
	user!: UserJsVm;
	clientItems = signal<SelectItem[]>([]);

	// S2N-3242: the agency switcher filters in place now. The list is rendered inline in the
	// dialog rather than in a floating overlay panel, because an overlay panel would not take
	// touch scrolling on iOS, so the whole list was unreachable on a phone.
	agencyFilter = signal<string>('');

	// Collapsed until asked for, so the dialog opens showing the current agency rather than a
	// wall of 180 of them.
	agencyListOpen = signal<boolean>(false);

	readonly filteredClientItems = computed(() => {
		const search = this.agencyFilter().trim().toLowerCase();
		const items = this.clientItems();
		if (!search) { return items; }
		return items.filter(o => o.text?.toLowerCase().includes(search));
	});

	formGroup = SwapClientVm.Form;
	model = SwapClientVm.GetModel(this.formGroup);

	// Fa icons
	faIconName = { 
		faUser
		, faQuestionCircle
		, faShelves
		, faHouse
		, faStaffSnake
		, faArrowRight 
		, faSwap
        , faBarcodeRead
        , faGear
	};	

	navService = inject(NavigationService);

	// Inactivity timeout needs to be injected here to have a constant timeout going in the background
	constructor() {
		this.updateCheck();
		this.configureDatadog();
		this.configureModal(); // Set default modal settings
		this.setIsLoggedInHtmlClass(); // Handle is-logged-in HTML class
		// Dirty AngularJS style global router used in index.html
		(globalThis as any)['$router'] = this.router;
	}

	@HostListener('window:resize') onResize() { 
        this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth)); 
		if (this.navService.isMobile()) {
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.toggleNavFixed(false);
			this.navService.toggleNavOpen(false);
		}
	}

	@HostListener("window:online", ["$event"]) onlineEvent(event: any) { this.uploadUsersFromLocalDb(event); }

	ngOnInit() {
        this.route.queryParamMap.subscribe(params => {
            const launcher = params.get('launcher');
            // Redirect to a specific route
            if (launcher === 'pwa') { this.router.navigate(['/dashboard']); }
        });
        this.router.events.pipe(
            map(navigationLoadingState)
            , filter((e): e is boolean => e !== null)
        ).subscribe(e => {
            // S2N-3226: navigation no longer registers or updates the service worker; see
            // ServiceWorkerRegistrationService for the single registration
            if (!e) { this.setActiveFooterItem(); }
            this.isLoading.set(e);
        });

        // Cover the first paint too — the subscription above only fires on later navigations
        this.setActiveFooterItem();

		this.navService.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
        this.isMobile.set(GlobalService.setIsMobile(window.innerWidth));
		// Check isNavFixed global variable on init for pages without sidenav
		if (this.gvs.isNavFixed()) {
			this.navService.isNavFixed.set(true);
			this.navService.isNavOpen.set(true);			
		}
		this.sec.user$.subscribe(user => {
			this.user = user;
			this.gvs.setClientCustomNames(this.user);
			// Profile matching needs the signed-in user's id, which can arrive after first paint
			this.setActiveFooterItem();
		}); // For 'My Profile' nav functionality

		this.badBrowserCheck(); 
		this.onChangePasswordScreen = globalThis.location.href.includes("code=fromEmail");

		// Remember if nav is fixed
		if (this.navService.isMobile()) { 
			localStorage.setItem("isNavFixed", "false"); 
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		} else if (localStorage.getItem("isNavFixed") === "true") {
            this.navService.isNavFixed.set(true);
            this.navService.isNavOpen.set(true);
        } else {
            this.navService.isNavFixed.set(false);
            this.navService.isNavOpen.set(false);
        }

		if (globalThis.location.href.includes("code=fromEmail")) {
			this.navService.isNavFixed.set(false);
			this.navService.isNavOpen.set(false);
		}
		this.onChangePasswordScreen =  this.gvs.onChangePasswordScreen();

        // Set user theme
        this.setUserTheme();
	}

	ngAfterContentChecked() {
        this.isSysAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.SysAdmin);
        this.isAdmin = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Admin);
        this.isManager = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Manager);
        this.isProvider = this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Provider);
        this.isWitness.set(this.sec.hasAccess(o => o.permissionProfile == PermissionProfileEnum.Witness));
        this.setViewOnlyBodyClass(profileIsViewOnly(this.sec.getSecurity()?.permissionProfile));
        this.isApiUser = this.user?.userType == UserTypeEnum.ApiOnly;
		if (
			this.user?.clientLogoFilename !== "" 
			&& this.user?.clientLogoFilename !== undefined 
			&& this.user?.clientLogoFilename !== null
		) { this.fileLogoUrl = this.user?.clientLogoFilePath!; }
		this.showSwapClient = this.user?.clientCount > 1
			&& (
				!this.user?.isUsingSso
				|| this.user?.isInSsoCascadeCorporate
			);
    }

	ngDoCheck() { this.isLoggedIn.set(this.sec.isLoggedOn()); }	

	updateCheck(): void {
		this.sw.checkForUpdates();
		/* this.swUpdate.checkForUpdate()
			.then(() => this.updateCheckText = 'resolved')
			.catch(err => this.updateCheckText = `rejected: ${err.message}`); */
	}

	/** Set default ng-bootstrap modal options */
	configureModal() {
		// Disable ng bootstrap modal animations by default
		this.modalConfig.animation = false;
		this.modalConfig.ariaLabelledBy = 'modal-basic-title';
	}

	/** Sets the is-logged-in HTML class, which is used to hide the release notes links */
	setIsLoggedInHtmlClass() {
		// Add / remove is-logged-in class
		this.sec.user$.subscribe(user => {
			this.isLoggedIn.set(!!user);
			if (this.isLoggedIn()) { this.elRef.nativeElement.classList.add('is-logged-in'); } 
			else { this.elRef.nativeElement.classList.remove('is-logged-in'); }
		});
	}

	myBrowser() {
		if (
			navigator.userAgent.includes('Opera') 
			|| navigator.userAgent.includes('OPR')
		) { return 'Opera'; } 
		else if (navigator.userAgent.includes('Chrome')) { return 'Chrome'; } 
		else if (navigator.userAgent.includes('Safari')) { return 'Safari'; } 
		else if (navigator.userAgent.includes('Firefox')) { return 'Firefox'; } 
		else if (
			navigator.userAgent.includes('MSIE') 
			|| navigator.userAgent.includes('trident')
		) { return 'IE'; } 
		else { return 'unknown'; }
	}

	badBrowserCheck() {
		if (
			navigator.userAgent.includes('MSIE') 
			|| navigator.userAgent.includes('trident')
		) { this.ns.showError("Internet Explorer (IE) is an unsupported browser. Please use a supported browser.", { timeOut: 5000 }); }
	}

	configureDatadog() {
		if (pcgSettings?.mode?.toLowerCase() !== 'development') {
			datadogLogs.setGlobalContext({ env: 'S2' + pcgSettings.mode });
			datadogLogs.setGlobalContextProperty('referrer', document.referrer);
			datadogLogs.logger.setLevel('warn');

			this.initDataDog();
			datadogLogs.setUserProperty('id', this.getDatadogUser(this.sec.getUser()));
	
			this.sec.user$.subscribe(newUser => { 				
				this.initDataDog();
				datadogLogs.setUserProperty('id', this.getDatadogUser(newUser));
			});
		}
	}

	initDataDog() {
		try {
			datadogLogs.clearUser();
			const initConfig = datadogLogs.getInitConfiguration();
			if (
				initConfig === null 
				|| initConfig === undefined
			) { // stop datadog init if it is already initialized
				datadogLogs.init({
					clientToken: "pub7c911faf7993ab48b8e3603c61aed9c3"
					, trackingConsent: 'granted'
					, site: "us5.datadoghq.com"
					, service: "S2" + pcgSettings?.mode
					, version: "748ec8f"
					, env: "S2" + pcgSettings?.mode
					, forwardErrorsToLogs: true
					, sessionSampleRate: 100			
				});
			} 
		} catch (ex) { console.error('Failed to initialize datadog:', ex); }
	}

	getDatadogUser(userJs: UserJsVm): ddUser {
		let ddUser : ddUser = { 
			id: userJs?.id?.toString()
			, email: userJs?.email
			, name: `${userJs?.fullName}` 
		}
		return ddUser;
	}

	uploadUsersFromLocalDb(event: any) { // ran when user comes back online
		if (event.type == "online") {
			this.localDb.table("EditUserTbl").toArray()
				.then((response) => { 
					let filteredResponse = response.filter(o => o.isUpdatedOffline);
					if (filteredResponse?.length > 0) { // means there are entries that have been updated
						for (let x = 0; x < filteredResponse?.length; x++) {
							this.http.post("api/Admin/Users/SaveUser", filteredResponse[x])
								.subscribe((response: any) => { 
                                    this.localDbService.setLocalDbTable("EditUserTbl", response.model, false); 
                                }
							);
						}
					}
				}
			);
		}
	}

	/**
	 * Highlights the mobile footer item matching the current route. Section prefixes rather
	 * than exact URLs, so the item stays lit while the user works deeper inside that section
	 * (e.g. an inventory workflow under /inventory/**). Profile matches only the signed-in
	 * user's own edit page — an admin editing someone else is not "on Profile".
    */
	setActiveFooterItem() {
		const url = (this.router.url ?? '').split('?')[0].toLowerCase();
		this.isOnHome = url === '/' || url.startsWith('/dashboard');
		this.isOnInventories = url.startsWith('/inventory');
		this.isOnProfile = this.user?.id != null
			&& url.startsWith(`/admin/users/edit/details/${this.user.id}`.toLowerCase());
	}

    navigateToDashboard(nav: any) { this.router.navigateByUrl(nav.href); }
	toReleaseNotes() { this.router.navigateByUrl("/release-notes"); }
	takeToHome() { this.router.navigateByUrl('/dashboard'); }
	takeToInventory() { this.router.navigateByUrl('/inventory/list'); }
	takeToMyProfile() { this.router.navigateByUrl('/admin/users/edit/details/' + this.user?.id); }
    takeToCvReport() { this.router.navigateByUrl('/reports/control-value'); }
    takeToMyCompany() { this.router.navigateByUrl('/company/info/' + this.user?.clientId); }
	openKnowledgeBase() { window.open("https://support.schedule2.it/", "_blank"); }

	ngOnDestroy(): void { 
		this.isAdmin = false;
		this.isSysAdmin = false;
		this.isManager = false;
		this.isProvider = false;
		this.setViewOnlyBodyClass(false);
	}

	/** S2N-3076: keep the <body> `s2-view-only` class in step with the profile; a no-op when nothing changed. */
	private setViewOnlyBodyClass(isViewOnly: boolean) {
		if (isViewOnly === this.isViewOnly) { return; }
		this.isViewOnly = isViewOnly;
		if (isViewOnly) { this.renderer.addClass(this.document.body, 's2-view-only'); }
		else { this.renderer.removeClass(this.document.body, 's2-view-only'); }
	}

	// #region Change Customer
	getClients() { 
		if (this.user?.permissionId === PermissionRowIds.sysAdmin) { 
			this.http.get(`api/Select/Clients`).subscribe((items: any) => { this.clientItems.set(items); }); 
		} else if (this.user?.clientCount > 1) {
			this.http.get(`api/Select/ClientsByUser?id=${this.user.id}`).subscribe((items: any) => { this.clientItems.set(items); });
		}
		this.gvs.currentClientId.set(this.user?.currentClientId);
		this.currentClientId = this.gvs.currentClientId();
	}

	openChangeClientModal() {
		this.agencyFilter.set('');
		this.agencyListOpen.set(false);
		if (
			this.user?.permissionId === PermissionRowIds.sysAdmin
			|| this.user?.clientCount > 1
		) { this.getClients(); }	
		this.modalService.open(this.modalChangeClient, { beforeDismiss: () => { return true; } });
		this.model = SwapClientVm.GetModel(this.formGroup);
		this.formGroup?.controls?.eventId?.patchValue(this.user?.currentClientId);
	}

	// A plain method rather than a computed: currentClientId is an ordinary property, so a
	// computed would never see it change.
	selectedAgencyText(): string {
		return this.clientItems().find(o => o.value === this.currentClientId)?.text ?? 'Select an agency';
	}

	toggleAgencyList() {
		const opening = !this.agencyListOpen();
		if (opening) { this.agencyFilter.set(''); }
		this.agencyListOpen.set(opening);
		// The field only exists once the list has rendered, so focus it on the next tick.
		if (opening) { setTimeout(() => this.agencySearch?.nativeElement.focus()); }
	}

	// Picking one collapses the list again, so the dialog goes back to reading as the single
	// line it opened as, now naming the new agency.
	selectAgency(val: number) {
		this.changeClient(val);
		this.agencyListOpen.set(false);
	}

	changeClient(val: number) {
		this.currentClientId = val;
		this.model.clientId = this.currentClientId;
		this.formGroup?.controls?.clientId?.patchValue(this.currentClientId);
	}

	saveChangeClient(modal: { dismiss: () => void; }) {
		this.http.post('api/SetClient', this.formGroup.getRawValue())
			.subscribe(async (sm: any) => {
				if (sm.isSuccessful) { 
					if (sm.value !== null) {
						let newUserInfo: UserJsVm = sm.value?.user;	
						this.sec.setSecurity(sm.value?.token, newUserInfo, sm.value?.security);
						this.clientName = newUserInfo?.clientName!;
						this.gvs.currentClientName.set(newUserInfo?.clientName!);
						this.currentClientId = newUserInfo?.currentClientId;
						this.gvs.currentClientId.set(newUserInfo?.currentClientId!);
						this.gvs.setClientCustomNames(newUserInfo);
                        localStorage.setItem("inventorySites", "false");
                        // S2N-3126: the previous agency's cached API responses (and any protected
                        // bytes in them) must not survive into the new agency scope.
                        await this.sec.purgeManagedCaches();

						// use window.location.href to force refresh on redirect
						if (newUserInfo.needToResetPin === true) {
							globalThis.location.href = document.getElementsByTagName('base')[0].href
								+ `admin/users/edit/security/${newUserInfo.id}?r=1`;
						} else {
							globalThis.location.href = document.getElementsByTagName('base')[0].href + "dashboard";
						}
						modal.dismiss();
					}
				} else { this.ns.showError(sm?.message, {timeOut: 5000}); }
			}
		);
	}
	// #endregion

    setUserTheme() {
        if (this.user != null) {
            const theme: UserThemeEnum = this.user.userTheme;
            this.themeService.setTheme(theme);
        }
    }
}
